'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import {
  createStaff,
  deactivateStaff,
  fetchAdminUsers,
  fetchLastSyncRun,
  fetchUserAuditLog,
  resetStaffPassword,
  sendLoginDetails,
  updateStaff,
  type AdminUsersListParams,
  type CreateStaffInput,
  type UpdateStaffInput,
} from '@/features/admin/api/admin-users.api'
import { useApiFetch } from '@/features/auth'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

/** `enabled` lets a shared component that's only sometimes admin (e.g. `client-detail.tsx`, mode
 * `'staff' | 'admin'`) defer this call until it actually is — `GET /admin/users` is
 * `@Roles('admin')`-only server-side, so calling it as staff would 403. */
export function useAdminUsers(
  params: AdminUsersListParams = {},
  options: { enabled?: boolean } = {},
) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.admin.users.list(params),
    queryFn: () => fetchAdminUsers(authedFetch, params),
    enabled: options.enabled ?? true,
  })
}

export function useCreateStaff() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateStaffInput) => createStaff(authedFetch, input),
    onSuccess: () => {
      // The backend upserts the local User row synchronously before responding, so the very
      // next fetch already has the new staff member — no need to wait for the Clerk webhook.
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users.all })
    },
  })
}

export function useUpdateStaff() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: UpdateStaffInput }) =>
      updateStaff(authedFetch, userId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users.all })
      toast({ variant: 'success', title: 'Staff profile updated' })
    },
    onError: toastError,
  })
}

export function useResetStaffPassword() {
  const authedFetch = useApiFetch()
  return useMutation({
    mutationFn: (userId: string) => resetStaffPassword(authedFetch, userId),
  })
}

export function useSendLoginDetails() {
  const authedFetch = useApiFetch()
  return useMutation({
    mutationFn: (userId: string) => sendLoginDetails(authedFetch, userId),
    // Explicit, not just relying on the global mutation default: a silent retry here would
    // email a second, different password (each call rotates the Clerk password) from one click.
    retry: 0,
  })
}

export function useDeactivateStaff() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => deactivateStaff(authedFetch, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users.all })
    },
    // Success toast stays in `admin-staff-view.tsx`'s `confirmRemove` — it needs the mutation's
    // `reassignedCaseCount` result to phrase the message, which isn't available in this hook.
    onError: toastError,
  })
}

/** `userId: null` keeps the query idle — the dialog is mounted before any row is picked. */
export function useUserAuditLog(userId: string | null) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.admin.users.auditLog(userId ?? ''),
    queryFn: () => fetchUserAuditLog(authedFetch, userId as string),
    enabled: userId !== null,
  })
}

/** The latest user-sync run (any source) — drives the "Last synced" line on the Staff page. */
export function useLastSyncRun() {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.admin.users.lastSyncRun,
    queryFn: () => fetchLastSyncRun(authedFetch),
  })
}
